import { yupResolver } from '@hookform/resolvers/yup';
import { CountryCode } from 'libphonenumber-js';
import React, { FC, useMemo } from 'react';
import { useForm, Controller, Resolver } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import CommonModal from 'src/components/CommonModal';
import InputText from 'src/components/InputText/InputText';
import PhoneInput from 'src/components/PhoneInput/PhoneInput';
import Radio from 'src/components/Radio/Radio';
import { REGULAR_EXPRESSION_NEW, inbondContactMethodList, inbondTypeList } from 'src/constants/common';
import { usePhone } from 'src/hooks/usePhone';
import * as yup from 'yup';

import { IInBondForm, IInBondModal } from './quoteDetailModal.interface';

const InBondModal: FC<IInBondModal> = ({ handleClose, handleInBond, defaultFormValues }) => {
    const { t } = useTranslation();
    const { isPhoneValid, isGetCountryCallingCode } = usePhone();

    const schema = yup.object().shape({
        inbondType: yup.string().required('Inbond type is required'),
        inbondName: yup.string().required('Inbond name is required'),
        inbondAddress: yup.string().required('Inbond address is required'),
        countryCode: yup.string(),
        inbondContactValue: yup
            .string()
            .required('Contact value is required')
            .when('inbondContactMethod', {
                is: 'phone-number',
                then: (yupSchema) =>
                    yupSchema.test('isValidPhone', function (value) {
                        const { countryCode } = this.parent;

                        if (value?.length === isGetCountryCallingCode(countryCode)?.length) {
                            return false;
                        }

                        const isValid = isPhoneValid(value, countryCode);

                        return isValid;
                    }),
                otherwise: (yupSchema) => yupSchema,
            })
            .when('inbondContactMethod', {
                is: 'email-address',
                then: (yupSchema) => yupSchema.matches(REGULAR_EXPRESSION_NEW.EMAIL, 'Invalid email format'),
                otherwise: (yupSchema) => yupSchema,
            }),
        inbondContactMethod: yup.string().required('Inbond contact method is required'),
    });

    const defaultValues = useMemo(
        () => ({
            inbondType: defaultFormValues?.inbondType || inbondTypeList[0].value,
            inbondName: defaultFormValues?.inbondName || '',
            inbondAddress: defaultFormValues?.inbondAddress || '',
            inbondContactValue: defaultFormValues?.inbondContactValue || '',
            inbondContactMethod: defaultFormValues?.inbondContactMethod || 'email-address',
            countryCode: defaultFormValues?.countryCode || 'CA',
        }),
        [defaultFormValues]
    );

    const methods = useForm<IInBondForm>({
        resolver: yupResolver(schema) as unknown as Resolver<IInBondForm>,
        defaultValues,
    });

    const {
        handleSubmit,
        control,
        watch,
        setValue,
        formState: { errors, isDirty },
    } = methods;

    const watchInbondContactMethod = watch('inbondContactMethod');

    const handleContactMethod = (onChange: any) => (e: any) => {
        onChange(e.target.value);

        if (e.target.value !== 'phone-number') {
            setValue('inbondContactValue', '');
        }
    };

    const handleNumberChange = (country: CountryCode, phone: string, code: string) => {
        if (phone) {
            setValue('countryCode', country);
            setValue('inbondContactValue', `+${code}${phone}`);
        }
    };

    const onSubmit = (data: any) => {
        handleInBond(data);
    };

    return (
        <CommonModal
            title={t('QuotingHub.Common.In_Bond_Details')}
            handleClose={handleClose}
            size={'max-w-[688px] overflow-unset'}
            isOverflow={false}
            secondaryBtnText={t('Common.Cancel')}
            primaryBtnText={t('Common.Save_Changes')}
            primaryBtnOnClick={handleSubmit(onSubmit)}
            secondaryBtnOnClick={handleClose}
            primaryBtnDisabled={!isDirty}
        >
            <div className="p-4">
                <div className="flex sm:flex-row flex-col flex-wrap gap-5 py-[12.5px] mb-5">
                    <Controller
                        name="inbondType"
                        control={control}
                        render={({ field: { value, onChange } }) => (
                            <>
                                {inbondTypeList.map((val: any, i) => (
                                    <div className="flex gap-2" key={`inbond_${i}`}>
                                        <Radio id={`inbond_${i}`} onChangeFunc={onChange} inputName={'inbondType'} value={val.value} checked={value === val.value} />

                                        <label htmlFor={`inbond_${i}`} className="text-textSecondary text-xs cursor-pointer font-medium">
                                            {val.name}
                                        </label>
                                    </div>
                                ))}
                            </>
                        )}
                    />
                </div>
                <div className="w-full mb-4">
                    <Controller
                        name="inbondName"
                        control={control}
                        render={({ field: { value, onChange } }) => (
                            <InputText
                                value={value}
                                onChangeFunc={onChange}
                                inputName="inbondName"
                                required={true}
                                placeholder={t('QuotingHub.Common.Enter_warehouse_or_carrier')}
                                className={`form_control ${errors.inbondName ? 'border border-red-500 border-solid' : ''}`}
                                label={t('QuotingHub.Common.Warehouse_Carrier_to_cancel_US_Bond')}
                                labelClassName="block mb-1.5"
                            />
                        )}
                    />
                </div>
                <div className="w-full mb-5">
                    <Controller
                        name="inbondAddress"
                        control={control}
                        render={({ field: { value, onChange } }) => (
                            <InputText
                                value={value}
                                onChangeFunc={onChange}
                                inputName="inbondAddress"
                                required={true}
                                placeholder={t('QuotingHub.QuotingDashboard.Enter_address')}
                                className={`form_control ${errors.inbondAddress ? 'border border-red-500 border-solid' : ''}`}
                                label={t('Common.Address')}
                                labelClassName="block mb-1.5"
                            />
                        )}
                    />
                </div>
                <div className="w-full mb-4">
                    <Controller
                        name="inbondContactValue"
                        control={control}
                        render={({ field: { value, onChange } }) =>
                            watchInbondContactMethod === 'phone-number' ? (
                                <PhoneInput
                                    onNumberChange={handleNumberChange}
                                    name="phone"
                                    label={'Phone'}
                                    required={true}
                                    errors={errors.inbondContactValue ? true : null}
                                    isShowErrorMsg={false}
                                    value={value || ''}
                                    maxLength={15}
                                />
                            ) : (
                                <InputText
                                    inputName="inbondContactValue"
                                    placeholder={watchInbondContactMethod === 'email-address' ? t('Common.Enter_email_address') : t('Common.Enter_fax_number')}
                                    required={true}
                                    className={`form_control ${errors.inbondContactValue ? 'border border-red-500 border-solid' : ''}`}
                                    label={watchInbondContactMethod === 'email-address' ? t('Common.Email') : 'Fax'}
                                    labelClassName="block mb-1.5"
                                    value={value}
                                    onChangeFunc={onChange}
                                />
                            )
                        }
                    />
                </div>
                <div className="flex sm:flex-row flex-col flex-wrap gap-5 py-[12.5px] ">
                    <Controller
                        name="inbondContactMethod"
                        control={control}
                        render={({ field: { value, onChange } }) => (
                            <>
                                {inbondContactMethodList.map((val: any, i) => (
                                    <div className="flex gap-2" key={`inbond_contact_${i}`}>
                                        <Radio
                                            id={`inbond_contact_${i}`}
                                            onChangeFunc={handleContactMethod(onChange)}
                                            inputName={'inbondContactMethod'}
                                            value={val.value}
                                            checked={value === val.value}
                                        />
                                        <label htmlFor={`inbond_contact_${i}`} className="text-textSecondary text-xs cursor-pointer font-medium uppercase">
                                            {val.name}
                                        </label>
                                    </div>
                                ))}
                            </>
                        )}
                    />
                </div>
            </div>
        </CommonModal>
    );
};
export default InBondModal;
